/* eslint-disable @typescript-eslint/ban-types */
import QueryTags from '../types/QueryTags'
import { EndpointBuilder } from '@reduxjs/toolkit/dist/query/endpointDefinitions'
import { BaseQueryFn, FetchArgs, FetchBaseQueryError, FetchBaseQueryMeta } from '@reduxjs/toolkit/query/react'
import { assign } from 'lodash'



const basePath = '/membership/users'

const usersEndpoints = (builder: EndpointBuilder<
  BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError, {}, FetchBaseQueryMeta>, QueryTags, 'data'>) =>
({
  fetchUsers: builder.query<any, {page: number, type: string, status?: string}>({
    query: ({page, type, status}) => {
      const params = new URLSearchParams()
      params.set('page', String(page))
      params.set('size', '10')
      params.set('userType', type)
      if (status) params.set('status', status)
      return { url: `${basePath}?${params.toString()}`, method: 'get' }
    },
    providesTags: ['users'],
  }),

  fetchMembersSummary: builder.query<any, {startDate?: string, endDate?: string}>({
    query: ({startDate, endDate} = {}) => {
      const params = new URLSearchParams()
      if (startDate) params.set('startDate', startDate)
      if (endDate) params.set('endDate', endDate)
      return { url: `${basePath}/members-summary?${params.toString()}`, method: 'get' }
    },
    providesTags: ['users'],
  }),

  fetchMemberIncomeReport: builder.query<any, {
    startDate?: string,
    endDate?: string,
    status?: string,
    sortBy?: string,
    sortDir?: 'asc' | 'desc',
    page?: number,
    size?: number,
  }>({
    query: ({ startDate, endDate, status, sortBy, sortDir, page = 0, size = 20 } = {}) => {
      const params = new URLSearchParams()
      params.set('page', String(page))
      params.set('size', String(size))
      if (startDate) params.set('startDate', startDate)
      if (endDate) params.set('endDate', endDate)
      if (status && status !== 'ALL') params.set('status', status)
      if (sortBy) params.set('sortBy', sortBy)
      if (sortDir) params.set('sortDir', sortDir)
      return { url: `${basePath}/member-income?${params.toString()}`, method: 'get' }
    },
    providesTags: ['users'],
  }),

  createUser: builder.mutation<any, any>({ 
    query: (body) => ({
      url: `${basePath}`,
      method: 'post',
      body,
    }),
    invalidatesTags: ['users'],
  }),

  updateUser: builder.mutation<any, any>({
    query: ({ id, body }) => ({
      url: `${basePath}/${id}`,
      method: 'put',
      body,
    }),
    invalidatesTags: ['users'],
  }),

  deleteUser: builder.mutation<any, any>({
    query: (id) => ({
      url: `${basePath}/${id}`,
      method: 'delete',
    }),
    invalidatesTags: ['users'],
  }),

  addMembership: builder.mutation<any, any>({
    query: (body) => ({
      url: `/memberships`,
      method: 'post',
      body,
    }),
    invalidatesTags: ['users'],
  }),

  searchAUser: builder.query<any, string>({
    query: (query) => ({
      url: `${basePath}?${query}`,
      method: 'get',
    }),
  }),

  addRental: builder.mutation<any, any>({
    query: ({ userId, body }) => ({
      url: `/${userId}/rentals`,
      method: 'post',   
      body,
    }),
    invalidatesTags: ['users'],
  }),

  assignMembershipToUser: builder.mutation<any, any>({
    query: ({ userId, body }) => ({
      url: `${basePath}/${userId}/membership`,
      method: 'post',
      body,
    }),
    invalidatesTags: ['users'],
  }),

  editAssignedUserMembership: builder.mutation<any, any>({
    query: ({ userId, body }) => ({
      url: `${basePath}/${userId}/membership`,
      method: 'put',
      body,
    }),
    invalidatesTags: ['users'],
  }),

  deleteAssignedUserMembership: builder.mutation<any, any>({
    query: ({ userId, membershipId }) => ({
      url: `${basePath}/${userId}/membership/${membershipId}`,
      method: 'delete',
    }),
    invalidatesTags: ['users'],
  }),

  assignRentalToUser: builder.mutation<any, any>({
    query: ({ userId, body }) => ({
      url: `${basePath}/${userId}/rentals`,
      method: 'post',
      body,
    }),
    invalidatesTags: ['users'],
  }),

  deleteAssignedRental: builder.mutation<any, any>({
    query: ({ userId, rentalId }) => ({
      url: `${basePath}/${userId}/rentals/${rentalId}`,
      method: 'delete',
    }),
    invalidatesTags: ['users'],
  }),

  hasReturnedRental: builder.mutation<any, any>({
    query: ({ userId, rentalId, body }) => ({
      url: `${basePath}/${userId}/rentals/${rentalId}`,
      method: 'post',
      body: body,
    }),
    invalidatesTags: ['users'],
  }),

  getAssignedRentalDetails: builder.query<any, { userId: string | undefined; rentalId: string }>({
    query: ({ userId, rentalId }) => ({
      url: `${basePath}/${userId}/rentals/${rentalId}`,
      method: 'get',
    }),
  }),

  getUserTransactions: builder.query<any, string>({
    query: (userId) => ({
      url: `${basePath}/${userId}/transactions`,
      method: 'get',
    }),
  }),

  fetchUserPayments: builder.query<any, { userId: string | undefined; page: number; size: number }>({
    query: ({ userId, page, size }) => ({
      url: `${basePath}/${userId}/payments?page=${page}&size=${size}`,
      method: 'get',
    }),
  }),

  assignOfficeToUser: builder.mutation<any, any>({
    query: ({ userId, body }) => ({
      url: `${basePath}/${userId}/office`,
      method: 'post',
      body,
    }),
    invalidatesTags: ['users'],
  }),

  completeUserPayment: builder.mutation<any, any>({
    query: ({ paymentId, body }) => ({
      url: `${basePath}/${paymentId}/finish-payments`,
      method: 'post',
      body,
    }),
    invalidatesTags: ['users'],
  }),

  cancelUserRental: builder.mutation<any, any>({
    query: ({ userId, rentalId, body }) => ({
      url: `${basePath}/${userId}/cancel-rentals/${rentalId}`,
      method: 'post',
      body,
    }),
    invalidatesTags: ['users'],
  }),

  editMembershipImage: builder.mutation<any, any>({
    query: ({ userId, body }) => ({
      url: `${basePath}/${userId}/image`,
      method: 'put',
      body,
    }),
    invalidatesTags: ['users'],
  }),

  changeUserType: builder.mutation<any, { userId: string; userType: 'MEMBER' | 'CASUAL' }>({
    query: ({ userId, userType }) => ({
      url: `${basePath}/${userId}/user-type?userType=${userType}`,
      method: 'put',
    }),
    invalidatesTags: ['users'],
  }),

})

export default usersEndpoints



